/* eslint semi: 1 */
import { defineStore } from 'pinia';
import { proxyException } from '@/utils/communication';
import { Dependencies } from '@/globals/utils';
import type { components } from '@base-shared/types/schedule.openapi';
import type { AxiosError } from 'axios';

type ScheduleSchema = components['schemas'];

export type Schedule = Omit<ScheduleSchema['ScheduleItem'], 'scheduleTags'>;
export type PagedScheduleResponse = ScheduleSchema['ListSchedulesResponse'];

interface ScheduleState {
  schedules: Schedule[];
  isFetching: boolean;
}

type FetchSchedulesParams = Partial<components['parameters']> | null;

type FetchPageResult =
  | { success: true; data: PagedScheduleResponse }
  | { success: false; error: AxiosError };

type FetchResult =
  | { success: true; data: Schedule[] }
  | { success: false; error: AxiosError };

export const scheduleStore = defineStore('schedule-store', {
  state: (): ScheduleState => ({
    schedules: [],
    isFetching: false
  }),
  actions: {
    /**
     * Aggregates schedules with the same scheduleId by merging their tags and features.
     *
     * If there are schedules with the same scheduleId,
     * it means that they are the same schedule but with
     * different tags (because of pagination).
     *
     * In this case, we need to aggregate their tags and
     * features into one schedule object only for smart-tags
     * or starts with`tag-` schedules.
     * @param {Schedule[]} schedules - An array of schedule objects to be aggregated.
     * @returns {Schedule[]} - An array of aggregated schedule objects.
     */
    aggregateAccumulatedSchedules (schedules: Schedule[]): Schedule[] {
      if (!schedules?.length) return [];
      const aggregated = new Map<string, Schedule>();

      schedules.forEach((item) => {
        if (!aggregated.has(item.scheduleId)) {
          aggregated.set(item.scheduleId, item);
        } else {
          const existing = aggregated.get(item.scheduleId)!;
          if (existing.scheduleId === 'smart-tags' || existing.scheduleId.startsWith('tag-')) {
            existing.tags = [...new Set([
              ...(existing.tags ?? []),
              ...(item.tags ?? []),
            ])];

            existing.features = [...new Set([
              ...(existing.features ?? []),
              ...(item.features ?? []),
            ])];
          }

          aggregated.set(item.scheduleId, existing);
        }
      });

      return Array.from(aggregated.values());
    },

    /**
     * Fetches a single page of schedules from the API.
     * All query parameters are passed directly to the endpoint.
     *
     * @param {FetchSchedulesParams} params - Query parameters for the request.
     * @returns {Promise<FetchPageResult>}
     */
    async fetchSchedules (params: FetchSchedulesParams = null): Promise<FetchPageResult> {
      const $api = Dependencies.inject('$api') as { get: (url: string, config?: unknown) => Promise<{ data: PagedScheduleResponse }> };

      const baseUrl = `${CONFIG.API_BASE_URL}/schedules`;

      const { ok: response, error } = await proxyException(() => $api.get(
        baseUrl,
        { params: params ?? {} }
      )) as { ok: { data: PagedScheduleResponse } | null; error: AxiosError };

      if (error) {
        return { success: false, error };
      }

      return { success: true, data: response!.data };
    },

    /**
     * This will get List of User Schedules that will be used for media publishing
     * It will keep fetching schedules till there are no more schedules to fetch (pagination)
     */
    async fetchAllSchedules (isMediaComponentAsAlert = false): Promise<FetchResult> {
      this.$patch({
        schedules: [],
        isFetching: true,
      });

      // Go over paginated results till there'll be no more schedules to fetch
      const accumulated: Schedule[] = [];
      let nextToken: string | undefined;
      let fetchError: AxiosError | undefined;

      do {
        const result = await this.fetchSchedules({
          max: 300,
          ...(isMediaComponentAsAlert ? { alert: true as const } : {}),
          ...(nextToken ? { nextToken } : {}),
        });

        if (!result.success) {
          fetchError = result.error;
          this.$patch({
            isFetching: false,
          });
          break;
        }

        accumulated.push(...result.data.items);
        nextToken = result.data.nextToken;
      } while (nextToken);

      if (fetchError) {
        return { success: false, error: fetchError };
      }

      this.$patch({
        schedules: this.aggregateAccumulatedSchedules(accumulated),
        isFetching: false,
      });

      return { success: true, data: this.schedules };
    },

    /**
     * Fetches a single page of schedules with extended details.
     * Used by the schedule list page and search bar for display purposes.
     * Callers manage their own pagination state using the returned `nextToken`.
     *
     * @param {FetchSchedulesParams} params - Query parameters including start, dur, width, search, tags, nextToken, etc.
     * @returns {Promise<FetchPageResult>}
     */
    async fetchExtendedSchedules (params: FetchSchedulesParams): Promise<FetchPageResult> {
      return this.fetchSchedules({
        extended: true,
        use_qsa: true,
        ...params,
      });
    }
  }
});

export type ScheduleStore = ReturnType<typeof scheduleStore>;
